import { Component } from '@theme/component';
import { fetchConfig, lockScroll, unlockScroll, onAnimationEnd, isClickedOutside } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} ShopAndBuyVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {string} price
 * @property {string | null} [compare_at_price]
 * @property {string[]} options
 * @property {string | null} [featured_image]
 */

/**
 * @typedef {object} ShopAndBuyProductData
 * @property {number} id
 * @property {string} title
 * @property {string} url
 * @property {string | null} [featured_image]
 * @property {string | null} [thumbnail]
 * @property {string | null} [video_url]
 * @property {string | null} [media_url]
 * @property {boolean} has_only_default_variant
 * @property {Array<{name: string, position: number, values: string[]}>} options
 * @property {ShopAndBuyVariant[]} variants
 * @property {string} price
 * @property {string | null} [compare_at_price]
 */

/**
 * @typedef {object} ShopAndBuyCardRefs
 * @property {HTMLButtonElement} [expandButton]
 * @property {HTMLButtonElement} [collapseButton]
 * @property {HTMLElement} [expandedPanel]
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLButtonElement} [expandedAddButton]
 * @property {HTMLSelectElement[]} [optionSelects]
 */

/**
 * Individual shoppable card with variant expand + add to cart.
 * @extends {Component<ShopAndBuyCardRefs>}
 */
class ShopAndBuyCardComponent extends Component {
  /** @type {ShopAndBuyProductData | null} */
  #product = null;

  /** @type {Record<number, string>} */
  #selectedOptions = {};

  connectedCallback() {
    super.connectedCallback();
    this.#loadProductData();
  }

  #loadProductData() {
    try {
      const script = this.querySelector('script[type="application/json"]');
      if (script instanceof HTMLScriptElement) {
        this.#product = JSON.parse(script.textContent || '{}');
      }
    } catch (error) {
      console.error('[shop-and-buy] Failed to parse product data', error);
      this.#product = null;
    }

    if (!this.#product) return;

    if (!this.#product.has_only_default_variant) {
      this.#product.options.forEach((option, index) => {
        const select = this.querySelector(`[data-option-index="${index}"]`);
        if (select instanceof HTMLSelectElement && select.value) {
          this.#selectedOptions[index] = select.value;
        } else if (option.values[0]) {
          this.#selectedOptions[index] = option.values[0];
        }
      });
      this.#syncVariantState();
    }
  }

  /**
   * Opens the popup for this card.
   * @param {Event} [event]
   */
  openPopup = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.#product) this.#loadProductData();
    if (!this.#product) return;

    const section = /** @type {ShopAndBuyComponent | null} */ (this.closest('shop-and-buy-component'));
    section?.openPopup(this.#product);
  };

  /**
   * Expands the variant selection panel.
   * @param {Event} [event]
   */
  expandVariants = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.classList.add('shop-and-buy-card--expanded');
    this.setAttribute('aria-expanded', 'true');
  };

  /**
   * Collapses the variant selection panel.
   * @param {Event} [event]
   */
  collapseVariants = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.classList.remove('shop-and-buy-card--expanded');
    this.setAttribute('aria-expanded', 'false');
  };

  /**
   * Handles option dropdown changes.
   * @param {Event} event
   */
  onOptionChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const index = Number(select.dataset.optionIndex);
    if (Number.isNaN(index)) return;

    this.#selectedOptions[index] = select.value;
    this.#syncVariantState();
  };

  /**
   * Adds the selected (or first available) variant to cart.
   * @param {Event} [event]
   */
  addToCart = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.#product) this.#loadProductData();

    const variant = this.#getCurrentVariant();
    if (!variant?.available) {
      if (this.#product && !this.#product.has_only_default_variant) {
        this.expandVariants();
      }
      return;
    }

    const buttons = [this.refs.addButton, this.refs.expandedAddButton].filter(Boolean);
    buttons.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = true;
    });

    try {
      await this.#addVariantToCart(variant.id);
      buttons.forEach((btn) => {
        if (btn instanceof HTMLButtonElement) {
          btn.dataset.added = 'true';
          setTimeout(() => {
            btn.dataset.added = 'false';
            btn.disabled = !variant.available;
          }, 1500);
        }
      });
    } catch (error) {
      console.error('[shop-and-buy] Add to cart failed', error);
      buttons.forEach((btn) => {
        if (btn instanceof HTMLButtonElement) btn.disabled = !variant.available;
      });
    }
  };

  /** @returns {ShopAndBuyVariant | null} */
  #getCurrentVariant() {
    if (!this.#product) return null;

    if (this.#product.has_only_default_variant) {
      return this.#product.variants[0] ?? null;
    }

    return (
      this.#product.variants.find((variant) =>
        variant.options.every((value, index) => value === this.#selectedOptions[index])
      ) ?? null
    );
  }

  #syncVariantState() {
    const variant = this.#getCurrentVariant();
    if (!variant) return;

    const priceEls = this.querySelectorAll('[data-shop-buy-price]');
    priceEls.forEach((el) => {
      el.innerHTML = '';
      const price = document.createElement('span');
      price.className = 'shop-and-buy-card__price';
      price.textContent = variant.price;
      el.appendChild(price);

      if (variant.compare_at_price) {
        const compare = document.createElement('span');
        compare.className = 'shop-and-buy-card__compare-price';
        compare.textContent = variant.compare_at_price;
        el.appendChild(compare);
      }
    });

    const thumb = this.querySelector('[data-shop-buy-thumb]');
    if (thumb instanceof HTMLImageElement && variant.featured_image) {
      thumb.src = variant.featured_image;
    }

    const available = Boolean(variant.available);
    [this.refs.addButton, this.refs.expandedAddButton].forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.disabled = !available;
      const label = btn.querySelector('[data-add-label]');
      if (label) {
        label.textContent = available
          ? btn.dataset.addText || 'Add to cart'
          : btn.dataset.soldOutText || 'Sold out';
      }
    });

    this.dataset.variantId = String(variant.id);
  }

  /**
   * @param {number} variantId
   */
  async #addVariantToCart(variantId) {
    const cartAddUrl = Theme?.routes?.cart_add_url || '/cart/add.js';
    const cartUrl = Theme?.routes?.cart_url || '/cart';
    const sectionIds = this.#getCartSectionIds();

    /** @type {{promise: Promise<unknown>, resolve: Function, reject: Function} | null} */
    let deferredEventPromise = null;

    try {
      deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );
    } catch (error) {
      console.warn('[shop-and-buy] Cart event dispatch skipped', error);
      deferredEventPromise = null;
    }

    const fetchCfg = fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });

    const response = await fetch(cartAddUrl, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    }).then((res) => res.json());

    if (response.status) {
      try {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: response.description,
              errors: response.errors,
            },
          })
        );
      } catch {
        // ignore
      }

      if (deferredEventPromise) {
        const ajaxCart = await fetch(`${cartUrl}.json`).then((res) => res.json());
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'shop-and-buy-card-component',
            itemCount: 1,
            productId: String(this.#product?.id ?? ''),
          },
        });
      }

      throw new Error(response.description || response.message || 'Add to cart failed');
    }

    const ajaxCart = await fetch(`${cartUrl}.json`).then((res) => res.json());

    if (deferredEventPromise) {
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'shop-and-buy-card-component',
          itemCount: 1,
          productId: String(this.#product?.id ?? ''),
          sections: response.sections,
        },
      });
    }

    // Open cart drawer when available
    try {
      const openCart = window.Shopify?.actions?.openCart;
      if (openCart?.run) {
        await openCart.run();
      } else {
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof /** @type {any} */ (drawer).open === 'function') {
          /** @type {any} */ (drawer).open();
        }
      }
    } catch {
      // Cart still updated even if drawer open fails
    }
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }
}

/**
 * @typedef {object} ShopAndBuyRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLVideoElement} [video]
 * @property {HTMLImageElement} [image]
 * @property {HTMLImageElement} [shopThumb]
 * @property {HTMLElement} [shopTitle]
 * @property {HTMLElement} [shopPrice]
 * @property {HTMLAnchorElement} [shopLink]
 * @property {HTMLButtonElement} [muteButton]
 */

/**
 * Section root: owns the popup modal and card coordination.
 * @extends {Component<ShopAndBuyRefs>}
 */
class ShopAndBuyComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {ShopAndBuyProductData | null} */
  #activeProduct = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog?.addEventListener('cancel', this.#onDialogCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#onDialogCancel);
  }

  /**
   * @param {ShopAndBuyProductData | null} product
   */
  openPopup(product) {
    if (!product) return;

    this.#activeProduct = product;
    const { dialog, video, image, shopThumb, shopTitle, shopPrice, shopLink, muteButton } = this.refs;

    if (shopTitle) shopTitle.textContent = product.title;
    if (shopPrice) shopPrice.textContent = product.price;
    if (shopThumb instanceof HTMLImageElement) {
      if (product.thumbnail) {
        shopThumb.src = product.thumbnail;
        shopThumb.alt = product.title;
        shopThumb.hidden = false;
      } else {
        shopThumb.removeAttribute('src');
        shopThumb.hidden = true;
      }
    }
    if (shopLink instanceof HTMLAnchorElement) {
      shopLink.href = product.url || '#';
    }

    const hasVideo = Boolean(product.video_url);
    if (video instanceof HTMLVideoElement) {
      if (hasVideo) {
        video.hidden = false;
        video.muted = true;
        video.poster = product.media_url || product.featured_image || '';
        if (video.src !== product.video_url) {
          video.src = product.video_url || '';
        }
        video.load();
        video.play().catch(() => {});
      } else {
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.hidden = true;
      }
    }

    if (image instanceof HTMLImageElement) {
      if (!hasVideo && (product.media_url || product.featured_image)) {
        image.src = product.media_url || product.featured_image || '';
        image.alt = product.title;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (muteButton instanceof HTMLButtonElement) {
      muteButton.hidden = !hasVideo;
      muteButton.setAttribute('aria-pressed', 'true');
      muteButton.dataset.muted = 'true';
    }

    if (!dialog.open) {
      lockScroll(dialog);
      dialog.showModal();
    }
  }

  /**
   * Closes the popup modal.
   * @param {Event} [event]
   */
  closePopup = async (event) => {
    event?.preventDefault();
    const { dialog, video } = this.refs;
    if (!dialog?.open) return;

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });

    if (video instanceof HTMLVideoElement) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    unlockScroll(dialog);
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#activeProduct = null;
  };

  /**
   * Closes when clicking the backdrop.
   * @param {MouseEvent} event
   */
  onBackdropClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closePopup(event);
    }
  };

  /**
   * Toggles video mute state.
   * @param {Event} [event]
   */
  toggleMute = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const { video, muteButton } = this.refs;
    if (!(video instanceof HTMLVideoElement) || !(muteButton instanceof HTMLButtonElement)) return;

    video.muted = !video.muted;
    muteButton.dataset.muted = String(video.muted);
    muteButton.setAttribute('aria-pressed', String(video.muted));
  };

  #onDialogCancel = (event) => {
    event.preventDefault();
    this.closePopup();
  };
}

if (!customElements.get('shop-and-buy-card-component')) {
  customElements.define('shop-and-buy-card-component', ShopAndBuyCardComponent);
}

if (!customElements.get('shop-and-buy-component')) {
  customElements.define('shop-and-buy-component', ShopAndBuyComponent);
}
